import { Copy, Globe, Trash2, Wrench } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toolToast } from '../utils/toolToast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmModal,
  LoadingSpinner,
} from '../components/ui';
import { DuplicateToolModal } from '../components/tools/DuplicateToolModal';
import { useAuth, usePage } from '../contexts';
import { toolService } from '../services';
import { ToolListResponse } from '../services/ToolService';
import { ToolTemplate, ToolTemplateListItem } from '../types';

const GROUP_LIMIT = 6;
const DEFAULT_PAGE_SIZE = 200;
type ToolsViewMode = 'showcase' | 'mine';

const Tools: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { setPageInfo } = usePage();

  const initialView: ToolsViewMode =
    searchParams.get('view') === 'showcase' ? 'showcase' : 'mine';
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicatingToolId, setDuplicatingToolId] = useState<string | null>(null);
  const [publishingToolId, setPublishingToolId] = useState<string | null>(null);
  const [duplicateToolName, setDuplicateToolName] = useState('');
  const [toolToDuplicate, setToolToDuplicate] = useState<ToolTemplateListItem | null>(null);
  const [toolToDelete, setToolToDelete] = useState<ToolTemplateListItem | null>(null);
  const [activeView, setActiveView] = useState<ToolsViewMode>(initialView);
  const [toolsData, setToolsData] = useState<ToolListResponse>({
    items: [],
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total: 0,
    total_pages: 1,
  });

  const fetchTools = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await toolService.list({
        page: 1,
        page_size: DEFAULT_PAGE_SIZE,
      });
      setToolsData(response);
    } catch {
      setToolsData(prev => ({ ...prev, items: [] }));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageInfo(t('tools.pageTitle'), t('tools.pageSubtitle'));
  }, [setPageInfo, t]);

  useEffect(() => {
    void fetchTools();
  }, [fetchTools]);

  useEffect(() => {
    const viewFromUrl: ToolsViewMode =
      searchParams.get('view') === 'showcase' ? 'showcase' : 'mine';
    setActiveView(current => (current === viewFromUrl ? current : viewFromUrl));
  }, [searchParams]);

  useEffect(() => {
    if (activeView !== 'showcase') {
      setToolToDuplicate(null);
      setDuplicateToolName('');
    }
  }, [activeView]);

  const isSuperAdmin = useMemo(() => {
    const roles = user?.roles ?? [];
    return roles.includes('superadmin');
  }, [user?.roles]);

  const handleChangeView = (nextView: ToolsViewMode): void => {
    setActiveView(nextView);
    setSearchParams({ view: nextView });
  };

  const getErrorMessage = (error: unknown, fallbackKey: string): string => {
    if (error && typeof error === 'object') {
      const maybeAxios = error as {
        response?: { data?: { detail?: string; message?: string } };
        message?: string;
      };
      const detail = maybeAxios.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      const messageFromApi = maybeAxios.response?.data?.message;
      if (typeof messageFromApi === 'string' && messageFromApi.trim()) {
        return messageFromApi;
      }
      if (typeof maybeAxios.message === 'string' && maybeAxios.message.trim()) {
        return maybeAxios.message;
      }
    }

    return t(fallbackKey);
  };

  const handleDeleteTool = async (): Promise<void> => {
    if (!toolToDelete?.id) {
      return;
    }

    setIsDeleting(true);
    try {
      await toolService.delete(toolToDelete.id);
      toolToast.success(t('tools.deleteSuccess'));

      await fetchTools();
    } catch (error) {
      toolToast.error(getErrorMessage(error, 'tools.deleteError'));
    } finally {
      setIsDeleting(false);
      setToolToDelete(null);
    }
  };

  const buildDuplicateDefaultName = (sourceName: string): string =>
    `${String(sourceName || '').trim() || t('tools.defaultName')} (cópia)`;

  const handleOpenDuplicateModal = (
    event: React.MouseEvent,
    sourceTool: ToolTemplateListItem
  ): void => {
    if (activeView !== 'showcase') {
      return;
    }
    event.stopPropagation();
    if (!sourceTool.id) {
      return;
    }
    setToolToDuplicate(sourceTool);
    setDuplicateToolName(buildDuplicateDefaultName(sourceTool.name));
  };

  const handleCloseDuplicateModal = (): void => {
    if (duplicatingToolId) {
      return;
    }
    setToolToDuplicate(null);
    setDuplicateToolName('');
  };

  const handleDuplicateTool = async (): Promise<void> => {
    if (!toolToDuplicate?.id) {
      return;
    }
    const normalizedName = duplicateToolName.trim();
    if (!normalizedName) {
      toolToast.error(t('tools.nameRequired'));
      return;
    }
    setDuplicatingToolId(toolToDuplicate.id);
    try {
      const duplicatedTool = await toolService.duplicate(toolToDuplicate.id, normalizedName);
      toolToast.success(t('tools.duplicateSuccess'));
      navigate(`/ferramentas/${duplicatedTool.id}`);
      setToolToDuplicate(null);
      setDuplicateToolName('');
    } catch (error) {
      toolToast.error(getErrorMessage(error, 'tools.duplicateError'));
    } finally {
      setDuplicatingToolId(null);
    }
  };

  const mapToolResponseToListItem = (tool: ToolTemplate): ToolTemplateListItem => ({
    id: tool.id,
    organization_id: tool.organization_id,
    created_by_user_id: tool.created_by_user_id,
    source_tool_id: tool.source_tool_id ?? null,
    name: tool.name,
    tool_type: tool.tool_type,
    public: tool.public,
    visibility_scope: tool.visibility_scope,
    hidden: tool.hidden,
    is_active: tool.is_active,
    created_at: tool.created_at,
    updated_at: tool.updated_at,
  });

  const handleMakeToolPublic = async (
    event: React.MouseEvent,
    tool: ToolTemplateListItem
  ): Promise<void> => {
    event.stopPropagation();
    if (!tool.id || tool.public) {
      return;
    }
    if (!isSuperAdmin) {
      toolToast.error(t('tools.onlySuperAdminCanChangePublic'));
      return;
    }

    setPublishingToolId(tool.id);
    try {
      const duplicatedTool = await toolService.duplicate(tool.id, tool.name);
      const updatedTool = await toolService.update(duplicatedTool.id, {
        public: true,
        visibility_scope: 'public',
      });
      const normalizedUpdatedTool = mapToolResponseToListItem(updatedTool);
      setToolsData(prev => ({
        ...prev,
        items: prev.items.some(item => item.id === normalizedUpdatedTool.id)
          ? prev.items.map(item =>
              item.id === normalizedUpdatedTool.id ? normalizedUpdatedTool : item
            )
          : [normalizedUpdatedTool, ...prev.items],
      }));
      toolToast.success(t('tools.makePublicSuccess'));
    } catch (error) {
      toolToast.error(getErrorMessage(error, 'tools.makePublicError'));
    } finally {
      setPublishingToolId(null);
    }
  };

  const groupedTools = useMemo(() => {
    const groups = new Map<string, ToolTemplateListItem[]>();
    const filteredTools = toolsData.items.filter(tool => {
      if (activeView === 'mine') {
        return tool.created_by_user_id === user?.id && !tool.public;
      }
      return tool.public;
    });
    filteredTools.forEach(tool => {
      const typeKey = (tool.tool_type || 'geral').trim() || 'geral';
      if (!groups.has(typeKey)) {
        groups.set(typeKey, []);
      }
      groups.get(typeKey)?.push(tool);
    });
    return Array.from(groups.entries()).sort(([typeA], [typeB]) =>
      typeA.localeCompare(typeB, 'pt-BR')
    );
  }, [activeView, toolsData.items, user?.id]);

  const formatTypeLabel = (value: string) =>
    value
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, match => match.toUpperCase());

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary-600" />
            {t('tools.pageTitle')}
          </CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('tools.pageSubtitle')}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 p-1">
            <button
              type="button"
              onClick={() => handleChangeView('mine')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                activeView === 'mine'
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
              }`}
            >
              {t('tools.myToolsView')}
            </button>
            <button
              type="button"
              onClick={() => handleChangeView('showcase')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                activeView === 'showcase'
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
              }`}
            >
              {t('tools.showcaseView')}
            </button>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner size="lg" />
            </div>
          ) : groupedTools.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('tools.emptyState')}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {groupedTools.map(([toolType, tools]) => (
                <section key={toolType} className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatTypeLabel(toolType)}
                    </h3>
                    {tools.length > GROUP_LIMIT ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          navigate(
                            `/ferramentas/tipo/${encodeURIComponent(toolType)}?view=${activeView}`
                          )
                        }
                      >
                        Ver mais
                      </Button>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {tools.slice(0, GROUP_LIMIT).map(tool => (
                      <div
                        key={tool.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col justify-between gap-3 cursor-pointer hover:bg-orange-50/50 dark:hover:bg-orange-900/10 hover:border-orange-400 dark:hover:border-orange-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 transition-colors min-h-[140px]"
                        onClick={() => navigate(`/ferramentas/${tool.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/ferramentas/${tool.id}`);
                          }
                        }}
                      >
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {tool.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('tools.createdAt')}: {tool.created_at || '-'}
                          </p>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          {activeView === 'showcase' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={duplicatingToolId === tool.id}
                              loading={duplicatingToolId === tool.id}
                              onClick={event => handleOpenDuplicateModal(event, tool)}
                            >
                              <Copy className="h-3.5 w-3.5 mr-1.5" />
                              {t('tools.useTool')}
                            </Button>
                          ) : null}
                          {isSuperAdmin && !tool.public ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={publishingToolId === tool.id}
                              loading={publishingToolId === tool.id}
                              onClick={event => void handleMakeToolPublic(event, tool)}
                            >
                              <Globe className="h-3.5 w-3.5 mr-1.5" />
                              {t('tools.makePublicButton')}
                            </Button>
                          ) : null}
                          {tool.created_by_user_id === user?.id ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={event => {
                                event.stopPropagation();
                                setToolToDelete(tool);
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                              {t('tools.deleteTool')}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmModal
        isOpen={Boolean(toolToDelete)}
        onClose={() => setToolToDelete(null)}
        onConfirm={handleDeleteTool}
        title={t('tools.deleteConfirmTitle')}
        message={t('tools.deleteConfirmMessage', {
          name: toolToDelete?.name || '-',
        })}
        confirmText={t('tools.deleteTool')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={isDeleting}
      />
      <DuplicateToolModal
        isOpen={Boolean(toolToDuplicate)}
        toolName={duplicateToolName}
        isLoading={Boolean(duplicatingToolId)}
        onClose={handleCloseDuplicateModal}
        onToolNameChange={setDuplicateToolName}
        onConfirm={() => void handleDuplicateTool()}
      />
    </div>
  );
};

export default Tools;
